<template>
  <div class="container" :class="{ 'only-mirror-list': !hasFilteredMirror }">
    <AppBar class="details" app clipped-left height="88px">
      <template v-slot:title>
        HISTÓRICO DE ESPELHOS: {{ currentProgramName }}
      </template>

      <template v-slot:actions-right>
        <v-menu
          v-model="showStorageDateMenu"
          :close-on-content-click="false"
          :nudge-left="80"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="processingDateFormatted"
              label="Data de exibição"
              readonly
              v-bind="attrs"
              v-on="on"
              hint="MM/DD/YYYY format"
              filled
            />
          </template>
          <v-date-picker
            :value="processingDate"
            :allowed-dates="allowedDates"
            locale="pt-br"
            @change="handleInputProcessingDate"
          />
        </v-menu>
      </template>
    </AppBar>

    <Error
      v-if="noSelectedHistoricMirror"
      textTitle="Tão <span>vazio</span> por aqui... <br/>Onde estão os <span>espelhos</span>?!"
      :textDescription="
        `Não encontramos nenhuma retranca para esse espelho exibido no dia ${processingDateFormatted}.`
      "
      imageTitle="bored"
      hideFeedbackInfo
      class="no-folder-access"
    />

    <!-- Complete View -->
    <BaseLayout
      v-else-if="breakpoint.smAndUp"
      :drawer="drawer"
      :mobile="breakpoint.smOnly"
      :width-drawer="breakpoint.mdOnly ? 300 : 400"
      @change-drawer="drawer = $event"
    >
      <!-- Navigation -->
      <template v-slot:navigation-drawer>
        <MirrorSidebar
          v-if="!loadingHistoricMirror"
          hideActionScheduleFocus
          hideActionFocus
          :selectedSlugId="selectedSlug.id"
          :selectedMirror="selectedHistoricMirror"
          :filteredMirror="filteredHistoricMirror"
          :processingDate="processingDate"
          @openMirrorItemDetails="handleMirrorItemDetails"
          @schedule-focus="handleAutoScrollClick"
          @focus="handleFocusClick"
          @refresh="loadHistoricMirror"
          @drawer-click="drawer = false"
        />
      </template>

      <!-- Main -->
      <template v-slot:main>
        <div
          v-touch="{
            left: () => handlerPagination('next'),
            right: () => handlerPagination('previous'),
          }"
          class="container-touch"
        >
          <div class="d-flex align-center">
            <v-btn
              v-if="!drawer"
              icon
              @click="drawer = true"
              class="button-drawer-menu mr-3"
            >
              <v-icon color="primary">menu</v-icon>
            </v-btn>
            <MirrorNavigation
              class="mb-3"
              :slug="selectedSlug"
              :mirror="filteredHistoricMirror"
              @changeSelectedMirrorItem="selectedMirrorIdItem = $event"
            />
          </div>
          <MirrorDetails
            v-show="!loadingHistoricMirror"
            v-if="hasFilteredMirror"
            :slug="selectedSlug"
          />
        </div>
      </template>
    </BaseLayout>

    <!-- Simple View -->
    <MirrorSidebar
      v-else-if="!loadingHistoricMirror && breakpoint.xsOnly"
      style="padding-top:100px"
      hideActionScheduleFocus
      hideActionFocus
      :selectedSlugId="selectedSlug.id"
      :selectedMirror="selectedHistoricMirror"
      :filteredMirror="filteredHistoricMirror"
      :processingDate="processingDate"
      @openMirrorItemDetails="handleMirrorItemDetails"
      @schedule-focus="handleAutoScrollClick"
      @focus="handleFocusClick"
      @refresh="loadHistoricMirror"
      @drawer-click="drawer = false"
    />
    <LoadingOverlay :enabled="loadingHistoricMirror" />
  </div>
</template>

<script>
import BaseLayout from '../components/shared/BaseLayout.vue';
import AppBar from '@/components/shared/AppBar.vue';
import Error from '@/components/shared/Error.vue';
import { mapGetters, mapState } from 'vuex';
import { dateFormat, isoDateFormat, dateIsSameOrAfter } from '../utils/date';

import MirrorSidebar from '@/components/mirrors/MirrorSidebar.vue';
import { actions, categories } from '@/constants/analytics.js';
import LoadingOverlay from '@/components/shared/LoadingOverlay.vue';
import MirrorDetails from '@/components/mirrors/MirrorDetails/index.vue';
import MirrorNavigation from '@/components/mirrors/MirrorNavigation.vue';
import mirrorNavigation from '@/mixins/mirrorNavigation.js';

export default {
  name: 'HistoricMirrorDetail',
  mixins: [mirrorNavigation],
  components: {
    BaseLayout,
    MirrorNavigation,
    AppBar,
    Error,
    MirrorSidebar,
    LoadingOverlay,
    MirrorDetails,
  },
  data() {
    return {
      selectedMirrorIdItem: '',
      drawer: null,
      showStorageDateMenu: false,
      processingDate: '',
      processingDateFormatted: '',
    };
  },

  computed: {
    ...mapGetters('mirror', ['filteredHistoricMirror']),
    ...mapState({
      currentUser: state => state.user.currentUser,
      loadingHistoricMirror: state => state.mirror.loadingHistoricMirror,
      lastMirrorAccessed: state => state.mirror.lastMirrorAcessed,
      selectedHistoricMirror: state => state.mirror.selectedHistoricMirror,
      systemFoldersMirrorHistoric: state =>
        state.mirror.systemFoldersMirrorHistoric,
      selectNameHistoricMirror: state => state.mirror.selectNameHistoricMirror,
    }),
    noSelectedHistoricMirror() {
      return (
        !this.selectedHistoricMirror || !this.selectedHistoricMirror.length
      );
    },
    // usado no mixin mirrorNavigation
    filteredMirror() {
      return this.filteredHistoricMirror;
    },

    breakpoint() {
      return this.$vuetify.breakpoint;
    },

    selectedSlugId() {
      if (this.selectedMirrorIdItem) return this.selectedMirrorIdItem;
      if (this.lastMirrorAccessed) {
        const id = this.lastMirrorAccessed.replace('mirror-', '');
        if (this.hasStoryId(id)) return id;
      }
      if (this.hasFilteredMirror) return this.filteredHistoricMirror[0].id || 0;
      return 0;
    },
    // Usado por mixin mirrorNavigation
    slug() {
      return this.selectedSlug;
    },

    selectedSlug() {
      const slugId = this.selectedSlugId;
      if (!slugId) return {};
      const slugs = this.filteredHistoricMirror || [];
      const currentSlug = slugs.find(slug => slug.id === slugId);

      if (currentSlug) return currentSlug;
      return this.filteredHistoricMirror[0] || {};
    },

    hasFilteredMirror() {
      return (
        Array.isArray(this.filteredHistoricMirror) &&
        this.filteredHistoricMirror.length
      );
    },
    currentPlace() {
      return this.$route.params.place;
    },

    currentProgramName() {
      return this.selectedHistoricMirror && this.selectedHistoricMirror.length
        ? this.selectedHistoricMirror[0].program_name
        : this.selectNameHistoricMirror;
    },

    currentProgramTag() {
      return this.$route.params.short_name;
    },
  },
  created() {
    window.location = `${process.env.VUE_APP_URL_NEW_NH}/storage-mirrors`;
  },
  watch: {
    processingDate() {
      this.processingDateFormatted = this.formatDate(this.processingDate);
    },
  },
  methods: {
    allowedDates(date) {
      return dateIsSameOrAfter(date);
    },

    handleInputProcessingDate(value) {
      const currentProcessingDate = this.processingDate;
      this.processingDate = value;
      if (value !== currentProcessingDate) this.loadHistoricMirror();
      this.showStorageDateMenu = false;
    },

    initializeStorageDate() {
      if (Date.parse(this.$route.query.date))
        this.processingDate = this.$route.query.date;
      else this.processingDate = isoDateFormat(new Date());
    },

    formatDate(date) {
      if (!date) return null;
      return dateFormat(date);
    },

    // Usado por mixin mirrorNavigation
    changeSelectedMirrorItem(type) {
      this.selectedMirrorIdItem = this.getNextOrPreviousMirrorId(type);
    },

    hasStoryId(id) {
      return this.filteredHistoricMirror.some(mirror => mirror.id === id);
    },

    loadMirrorWhenCreated() {
      if (
        this.breakpoint.xsOnly &&
        this.filteredHistoricMirror.length &&
        this.filteredHistoricMirror[0].short_name ===
          this.$route.params.short_name &&
        this.filteredHistoricMirror[0].place === this.$route.params.place
      )
        return null;
      this.loadHistoricMirror();
    },

    loadHistoricMirror() {
      this.$store.dispatch('mirror/selectHistoricMirror', {
        mirrorShortName: this.$route.params.short_name,
        place: this.$route.params.place,
        filterDate: new Date(`${this.processingDate} 00:00:00`).toISOString(),
      });
    },

    handleFocusClick() {
      this.$store.dispatch('analyticsEvent', {
        category: categories.mirror,
        action: actions.focusClicked,
        label: `${this.currentPlace}-${this.currentProgramTag}`,
      });
      this.$store.dispatch(
        'mirror/getReporterAutoScrollTarget',
        this.currentUser.username,
        this.filteredHistoricMirror,
      );
    },

    handleAutoScrollClick() {
      this.$store.dispatch('analyticsEvent', {
        category: categories.mirror,
        action: actions.autoScrollClicked,
        label: `${this.currentPlace}-${this.currentProgramTag}`,
      });
      this.$store.dispatch(
        'mirror/getTimeAutoScrollTarget',
        this.filteredHistoricMirror,
      );
    },

    handleMirrorItemDetails(activity) {
      if (this.$vuetify.breakpoint.smAndUp) {
        this.selectedMirrorIdItem = activity.id;
      }
      //TODO: Não deve funcionar para mobile
      // else
      //   this.$router.push(
      //     `/mirror/detail/${activity.place}/${activity.short_name}/${activity.id}`,
      //   );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/theme-v2.scss';
@import '@/assets/scss/buttons.scss';
@import '@/assets/scss/fixes.scss';
.container-touch {
  min-height: calc(100vh - 98px);
}
.container {
  background-color: $color-background;
}
.button-drawer-menu {
  background: $color-white;
}

.nh-alert-text {
  font-size: 14px !important;
  margin-right: 10px;
}
::v-deep .v-input {
  .v-input__control {
    .v-input__slot {
      background: #fff !important;

      input {
        border-bottom: 1px solid $color-primary;
        margin-top: 20px !important;
        padding: 0 10px;
      }

      .v-text-field__slot {
        .v-label--active {
          left: 0px !important;
        }
      }

      &::before,
      &::after {
        border: none !important;
      }
    }
  }
}
</style>
